import { GridFilterModel } from '@mui/x-data-grid';
import { Mapper } from './Mapper';
import { GraphQLQuery } from '../types/filter';

// Helper: split a token into lowercase alphanumeric subtokens
const splitIntoSubtokens = (s: string): string[] =>
  s.toLowerCase().match(/[a-z0-9]+/g) ?? [];

export class GridFilterModelGraphQLQueryMapper<T> implements Mapper<
  GridFilterModel,
  GraphQLQuery<T>
> {
  map(model: GridFilterModel): GraphQLQuery<T> {
    const { items, logicOperator } = model;
    if (!items || items.length === 0) {
      return {} as GraphQLQuery<T>;
    }

    const mappedFilters = items
      .map(item => {
        const { field, value, operator } = item;
        // Allow value-less operators
        const operatorRequiresValue = operator !== 'isNullOrMissing';
        if (operatorRequiresValue && !value) return null;

        switch (operator) {
          case 'equals':
            return { [field]: { eq: value } } as GraphQLQuery<T>;
          case 'contains': {
            const text = String(value);
            const subs = splitIntoSubtokens(text);
            if (subs.length === 0) {
              // fallback to raw contains if nothing to split
              const lc = text.toLowerCase();
              return { [field]: { wildcard: `*${lc}*` } } as GraphQLQuery<T>;
            }
            if (subs.length === 1) {
              return {
                [field]: { wildcard: `*${subs[0]}*` },
              } as GraphQLQuery<T>;
            }
            return {
              and: subs.map(st => ({ [field]: { wildcard: `*${st}*` } })),
            } as unknown as GraphQLQuery<T>;
          }
          case 'startsWith': {
            const lc = String(value).toLowerCase();
            return { [field]: { wildcard: `${lc}*` } } as GraphQLQuery<T>;
          }
          case 'endsWith': {
            const lc = String(value).toLowerCase();
            return { [field]: { wildcard: `*${lc}` } } as GraphQLQuery<T>;
          }
          case 'isAnyOf':
            if (Array.isArray(value)) {
              return {
                or: value.map(v => ({
                  [field]: { eq: v },
                })),
              } as GraphQLQuery<T>;
            } else {
              throw new Error(
                "Value must be an array when using 'isAnyOf' operator."
              );
            }
          case 'isNullOrMissing':
            return { [field]: { exists: false } } as GraphQLQuery<T>;
          default:
            throw new Error(`Filter operator ${operator} is not supported.`);
        }
      })
      .filter(Boolean) as GraphQLQuery<T>[];

    if (mappedFilters.length === 1) {
      return mappedFilters[0]!;
    } else {
      return logicOperator === 'and'
        ? ({ and: mappedFilters } as GraphQLQuery<T>)
        : ({ or: mappedFilters } as GraphQLQuery<T>);
    }
  }
}
